import Button from '@atoms/Button/Button';
import CloseButton from '@atoms/CloseButton/CloseButton';
import FormCaptcha from '@atoms/FormCaptcha/FormCaptcha';
import FormInput from '@atoms/FormInput/FormInput';
import FormSelect from '@atoms/FormSelect/FormSelect';
import Icon from '@atoms/Icon/Icon';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import { ILabelValue } from '@models/ILabelValue';
import { ISignupForm } from '@models/ISignupForm';
import ISignupFormStatus from '@models/ISignupFormStatus';
import { ISignupFormValues } from '@models/ISignupFormValues';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import accountMarketingPreferencesSubmit from '@services/accountMarketingPreferencesSubmit';
import signupFormSubmit from '@services/signupFormSubmit';
import signupFormVerify from '@services/signupFormVerify';
import {
  EMAIL_REGEX,
  TEXT_INPUT_REGEX,
} from '@utils/constants/fieldsValidationsPatterns';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useRecaptchaChallengeDisplay from '@utils/hooks/useRecaptchaChallengeDisplay';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import qs from 'qs';
import { useEffect, useState } from 'react';
import * as yup from 'yup';

interface Props extends ISignupForm {
  readonly fullPage?: boolean;
  readonly productCode?: string;
  readonly recaptchaChallengeElementZIndex?: string;
  readonly trackingComponentName?: string;
  readonly onClose?: () => void;
}

const SignupForm = ({
  authenticatedUser,
  contactList = '',
  countries,
  countryInitial,
  countryLabel,
  countryPlaceholder,
  emailErrorMessage,
  emailLabel,
  emailPlaceholder,
  firstNameErrorMessage,
  firstNameLabel,
  firstNamePlaceholder,
  formSuccessSubtitle,
  formSuccessTitle,
  formVerifiedSubtitle,
  formVerifiedTitle,
  formErrorSubtitle,
  formErrorTitle,
  hideLanguage,
  languageInitial,
  languageLabel,
  languagePlaceholder,
  languages,
  lastNameErrorMessage,
  lastNameLabel,
  lastNamePlaceholder,
  legalText,
  recaptchaChallengeElementZIndex = '999',
  requiredErrorMessage,
  siteKey,
  signupLabel,
  submitEndpoint,
  text,
  title,
  trackingComponentName,
  verifyEndpoint,
  fullPage = false,
  productCode = '',
  updateMarketingPreferencesEndpoint,
  userId = '',
  userEmail = '',
  onClose,
}: Props) => {
  const [status, setStatus] = useState(ISignupFormStatus.Loading);
  const [page, setPage] = useState('');
  const { closeSignUpFormAriaLabel } = useAriaLabels();
  useRecaptchaChallengeDisplay(recaptchaChallengeElementZIndex);

  const submitHandler = async (values: ISignupFormValues) => {
    try {
      setStatus(ISignupFormStatus.Loading);

      const updatedValues: ISignupFormValues = {
        ...values,
        language: hideLanguage
          ? countries?.find((country) => country.value === values.country)
              ?.defaultLanguage || ''
          : values.language,
      };

      await signupFormSubmit(
        updatedValues,
        contactList,
        page,
        submitEndpoint,
        productCode
      );

      setStatus(ISignupFormStatus.Submitted);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
      console.log({ error });

      setStatus(ISignupFormStatus.Errored);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    // Sets the X-Referer to domain/locale on the newsletter signup
    const locale = window.location.pathname.split('/');
    const referer = `${window.location.origin}/${locale[1]}`;
    setPage(referer);

    if (status === ISignupFormStatus.Default && !productCode) {
      updateDataLayer({
        event: 'newsletterSubscribe',
        content_type: 'start',
        email: userEmail,
        userId,
        ...(trackingComponentName
          ? { component_name: trackingComponentName }
          : {}),
      });
    }

    if (status === ISignupFormStatus.Verified && !productCode) {
      updateDataLayer({
        event: 'newsletterSubscribe',
        content_type: 'subscribed',
        email: userEmail,
        userId,
      });
    }

    if (status === ISignupFormStatus.Submitted && productCode) {
      updateDataLayer({
        event: 'formSubmit',
        content_type: 'notify_when_available',
        email: userEmail,
        userId,
      });
    }
  }, [status, productCode]);

  useEffect(() => {
    const query = window.location.search;

    const verificationToken = qs.parse(query, {
      ignoreQueryPrefix: true,
      decoder: (s) => s,
    });

    const verifySignup = async () => {
      try {
        const verifyResponseStatus = await signupFormVerify(
          `${verificationToken.token}`,
          verifyEndpoint
        );
        const isVerificationSuccessful = verifyResponseStatus === 200;

        setStatus(
          isVerificationSuccessful
            ? ISignupFormStatus.Verified
            : ISignupFormStatus.Default
        );

        if (
          isVerificationSuccessful &&
          authenticatedUser &&
          updateMarketingPreferencesEndpoint
        )
          await accountMarketingPreferencesSubmit(
            updateMarketingPreferencesEndpoint,
            { allowGDPRContact: true }
          );
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
        console.log({ error });

        setStatus(ISignupFormStatus.Default);
      }
    };

    if (verificationToken.token && verifyEndpoint) {
      verifySignup();
    } else {
      setStatus(ISignupFormStatus.Default);
    }
  }, [authenticatedUser, updateMarketingPreferencesEndpoint, verifyEndpoint]);

  const findInitial = (initialValue: string, valueLabelArray: ILabelValue[]) =>
    valueLabelArray?.find((country) => country.value === initialValue)?.value ||
    '';

  return (
    <section
      className={classNames({ 'bg-white': fullPage })}
      data-testid="CT-05-B-Signup-Form"
    >
      <SoftLoadWrapper isAnimationDisabled={!fullPage}>
        <div
          className={classNames(
            fullPage
              ? 'container py-20 xl:py-40'
              : 'flex w-full justify-center py-16 xl:h-full xl:items-center',
            'signup-form text-black-100'
          )}
        >
          <div
            className={classNames(
              {
                'relative flex h-full w-full max-w-[675px] flex-col rounded-[10px] px-5 py-10 lg:px-13 lg:py-15 xl:min-w-[675px]':
                  !fullPage,
              },
              'h-full bg-white'
            )}
          >
            {status === ISignupFormStatus.Loading && (
              <LoadingSpinner extraClasses="min-h-[400px]" />
            )}
            {(status === ISignupFormStatus.Verified ||
              status === ISignupFormStatus.Submitted ||
              status === ISignupFormStatus.Errored) && (
              <div className="flex h-full flex-col items-center justify-center space-y-2.5 py-40 text-center">
                <Icon
                  name={`${
                    status === ISignupFormStatus.Errored ? 'close' : 'check'
                  }`}
                  size="3.125rem"
                  colorClassName={
                    status === ISignupFormStatus.Errored
                      ? 'text-red'
                      : 'text-yellow'
                  }
                  className="mb-4"
                  data-testid="CT-05-B-Signup-Form-Form-Icon"
                />

                {formSuccessTitle && status === ISignupFormStatus.Submitted && (
                  <p
                    className="h4"
                    role="status"
                    aria-live="polite"
                    data-testid="CT-05-B-Signup-Form-Form-Success-Title"
                  >
                    {formSuccessTitle}
                  </p>
                )}

                {formSuccessSubtitle &&
                  status === ISignupFormStatus.Submitted && (
                    <div
                      className="body-one"
                      data-testid="CT-05-B-Signup-Form-Form-Success-Subtitle"
                    >
                      {formSuccessSubtitle}
                    </div>
                  )}

                {formVerifiedTitle && status === ISignupFormStatus.Verified && (
                  <p
                    className="h4"
                    role="status"
                    aria-live="polite"
                    data-testid="CT-05-B-Signup-Form-Form-Verified-Title"
                  >
                    {formVerifiedTitle}
                  </p>
                )}

                {formVerifiedSubtitle &&
                  status === ISignupFormStatus.Verified && (
                    <p
                      className="body-one"
                      data-testid="CT-05-B-Signup-Form-Form-Verified-Subtitle"
                    >
                      {formVerifiedSubtitle}
                    </p>
                  )}

                {formErrorTitle && status === ISignupFormStatus.Errored && (
                  <p
                    className="h4"
                    aria-live="polite"
                    data-testid="CT-05-B-Signup-Form-Form-Error-Title"
                  >
                    {formErrorTitle}
                  </p>
                )}

                {formErrorSubtitle && status === ISignupFormStatus.Errored && (
                  <p
                    className="body-one"
                    data-testid="CT-05-B-Signup-Form-Form-Error-Subtitle"
                  >
                    {formErrorSubtitle}
                  </p>
                )}
              </div>
            )}

            <div
              className={classNames(
                {
                  ' xl:grid-cols-12': fullPage,
                  hidden: status !== ISignupFormStatus.Default,
                },
                status === ISignupFormStatus.Default && fullPage
                  ? 'xl:grid'
                  : 'block'
              )}
            >
              <div className={classNames({ 'col-span-5': fullPage })}>
                {title && (
                  <h2
                    className={classNames('mb-2.5 px-1 lg:mb-5', {
                      h4: !fullPage,
                    })}
                    data-testid="CT-05-B-Signup-Form-Title"
                  >
                    {title}
                  </h2>
                )}

                {text && (
                  <div
                    className={classNames(
                      { 'xl:text-grey-450': fullPage },
                      'body-one mb-7.5 px-1 lg:mb-5'
                    )}
                    data-testid="CT-05-B-Signup-Form-Text"
                  >
                    {text}
                  </div>
                )}
              </div>
              <Formik
                initialValues={{
                  firstName: '',
                  lastName: '',
                  email: '',
                  country: hideLanguage
                    ? ''
                    : findInitial(countryInitial, countries),
                  language: findInitial(languageInitial, languages),
                  captcha: '',
                }}
                onSubmit={(values) => submitHandler(values)}
                validationSchema={yup.object({
                  firstName: yup
                    .string()
                    .required(requiredErrorMessage)
                    .matches(TEXT_INPUT_REGEX, firstNameErrorMessage),
                  lastName: yup
                    .string()
                    .required(requiredErrorMessage)
                    .matches(TEXT_INPUT_REGEX, lastNameErrorMessage),
                  email: yup
                    .string()
                    .required(requiredErrorMessage)
                    .matches(EMAIL_REGEX, emailErrorMessage),
                  country: yup.string().required(requiredErrorMessage),
                  language: yup.string(),
                  captcha: yup.string().required(requiredErrorMessage),
                })}
              >
                {({ isValid, dirty }) => (
                  <Form
                    className={classNames({
                      'col-span-6 col-start-7': fullPage,
                    })}
                  >
                    <div
                      className={classNames(
                        { 'xl:grid-cols-2': fullPage },
                        'grid w-full gap-x-4 gap-y-5 px-1'
                      )}
                    >
                      <FormInput
                        name="firstName"
                        label={firstNameLabel}
                        placeholder={firstNamePlaceholder}
                        required
                        type="text"
                        data-testid="CT-05-B-Signup-Form-First-Name"
                      />

                      <FormInput
                        name="lastName"
                        label={lastNameLabel}
                        placeholder={lastNamePlaceholder}
                        required
                        type="text"
                        data-testid="CT-05-B-Signup-Form-Last-Name"
                      />

                      <FormInput
                        name="email"
                        label={emailLabel}
                        placeholder={emailPlaceholder}
                        required
                        type="email"
                        data-testid="CT-05-B-Signup-Form-Email"
                      />

                      <FormSelect
                        name="country"
                        label={countryLabel}
                        options={countries}
                        placeholder={countryPlaceholder}
                        required
                        dataTestId="CT-05-B-Signup-Form-Country"
                      />
                      {!hideLanguage && (
                        <FormSelect
                          name="language"
                          label={languageLabel}
                          options={languages}
                          placeholder={languagePlaceholder}
                          dataTestId="CT-05-B-Signup-Form-Language"
                        />
                      )}
                    </div>

                    <div className="mt-7.5">
                      {siteKey && (
                        <FormCaptcha
                          siteKey={siteKey}
                          dataTestId="CT-05-B-Signup-Form-ReCaptcha"
                        />
                      )}
                    </div>

                    {signupLabel && (
                      <Button
                        disabled={!isValid || !dirty}
                        className="mt-7.5"
                        type="submit"
                        dataTestId="CT-05-B-Signup-Form-Signup-Button"
                      >
                        {signupLabel}
                      </Button>
                    )}

                    {legalText && (
                      <div
                        dangerouslySetInnerHTML={{ __html: legalText }}
                        className="body-four mt-7.5 w-full content-area"
                        data-testid="CT-05-B-Signup-Form-Legal-Text"
                      />
                    )}
                  </Form>
                )}
              </Formik>
            </div>

            {onClose && (
              <CloseButton
                onClick={onClose}
                className="absolute top-4 right-4"
                ariaLabel={closeSignUpFormAriaLabel}
              />
            )}
          </div>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default SignupForm;
